import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Eye,
  Edit3,
  Download,
  Send,
  RefreshCw,
  Copy,
  Check,
  FileText,
  Code,
  TrendingUp,
  Info,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { toast } from 'react-hot-toast';

interface AIWriterPreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPublishSuccess?: () => void;
  article: {
    id: string;
    title: string;
    content: string;
    content_html: string;
    meta_description: string;
    keyword_density: any;
    word_count: number;
    seo_score?: number;
    status: string;
  };
  onUpdate?: (updatedArticle: any) => void;
}

export function AIWriterPreview({
  open,
  onOpenChange,
  article,
  onUpdate,
  onPublishSuccess,
}: AIWriterPreviewProps) {
  // Function to clean HTML content from markdown artifacts
  const cleanHtmlContent = (content: string | null | undefined) => {
    if (!content) return '';
    return content
      .replace(/^```html\n?|\n?```$/gm, '') // Remove ```html and ``` markers
      .replace(/^```\n?|\n?```$/gm, '') // Remove any remaining ``` markers
      .replace(/^Title:.*$/m, '') // Remove Title: line
      .replace(/^Meta Description:.*$/m, '') // Remove Meta Description: line
      .replace(/\\'/g, "'") // Fix escaped apostrophes
      .replace(/\\n/g, '') // Remove literal \n
      .replace(/This article adheres.*$/gm, '') // Remove OpenAI meta comments
      .trim();
  };

  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editedContent, setEditedContent] = useState(cleanHtmlContent(article.content_html));
  const [editedTitle, setEditedTitle] = useState(article.title);
  const [editedMetaDescription, setEditedMetaDescription] = useState(article.meta_description || '');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const handleCopy = () => {
    const htmlContent = cleanHtmlContent(article.content_html);
    navigator.clipboard.writeText(htmlContent);
    setCopied(true);
    toast.success('Content copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExport = () => {
    const htmlContent = cleanHtmlContent(article.content_html);
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${article.title.toLowerCase().replace(/\s+/g, '-')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Article exported');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`/api/ai-writer/articles/${article.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editedTitle,
          content_html: editedContent,
          meta_description: editedMetaDescription,
          status: 'draft',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save article');
      }

      const updatedArticle = await response.json();
      toast.success('Article saved successfully');
      setEditing(false);
      
      if (onUpdate) {
        onUpdate(updatedArticle);
      }
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const response = await authFetch(`/api/ai-writer/articles/${article.id}/publish`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to publish article');
      }

      if (onPublishSuccess) {
        onPublishSuccess();
      } else {
        toast.success('Article published successfully!');
        onOpenChange(false);
      }
    } catch (error) {
      console.error('Error publishing article:', error);
      toast.error('Failed to publish article');
    } finally {
      setPublishing(false);
    }
  };

  const calculateSeoDetails = () => {
    const details = {
      title: { score: 0, max: 25, status: '', message: '' },
      meta: { score: 0, max: 25, status: '', message: '' },
      content: { score: 0, max: 25, status: '', message: '' },
      keywords: { score: 0, max: 25, status: '', message: '' },
      total: 0
    };
    
    // Check title length (50-60 chars is ideal)
    const titleLength = article.title.length;
    if (titleLength >= 50 && titleLength <= 60) {
      details.title.score = 25;
      details.title.status = 'success';
      details.title.message = `Perfect length (${titleLength} chars)`;
    } else if (titleLength >= 40 && titleLength <= 70) {
      details.title.score = 15;
      details.title.status = 'warning';
      details.title.message = `Good length (${titleLength} chars), ideal is 50-60`;
    } else if (titleLength < 40) {
      details.title.score = 5;
      details.title.status = 'error';
      details.title.message = `Too short (${titleLength} chars), aim for 50-60`;
    } else {
      details.title.score = 5;
      details.title.status = 'error';
      details.title.message = `Too long (${titleLength} chars), aim for 50-60`;
    }
    
    // Check meta description (150-160 chars is ideal)
    const metaLength = article.meta_description?.length || 0;
    if (metaLength >= 150 && metaLength <= 160) {
      details.meta.score = 25;
      details.meta.status = 'success';
      details.meta.message = `Perfect length (${metaLength} chars)`;
    } else if (metaLength >= 120 && metaLength <= 170) {
      details.meta.score = 15;
      details.meta.status = 'warning';
      details.meta.message = `Good length (${metaLength} chars), ideal is 150-160`;
    } else if (metaLength < 120) {
      details.meta.score = 5;
      details.meta.status = 'error';
      details.meta.message = `Too short (${metaLength} chars), aim for 150-160`;
    } else {
      details.meta.score = 5;
      details.meta.status = 'error';
      details.meta.message = `Too long (${metaLength} chars), aim for 150-160`;
    }
    
    // Check word count (1000+ is good)
    const wordCount = article.word_count || 0;
    if (wordCount >= 1000) {
      details.content.score = 25;
      details.content.status = 'success';
      details.content.message = `Excellent length (${wordCount} words)`;
    } else if (wordCount >= 750) {
      details.content.score = 15;
      details.content.status = 'warning';
      details.content.message = `Good length (${wordCount} words), aim for 1000+`;
    } else if (wordCount >= 500) {
      details.content.score = 10;
      details.content.status = 'warning';
      details.content.message = `Acceptable (${wordCount} words), aim for 1000+`;
    } else {
      details.content.score = 5;
      details.content.status = 'error';
      details.content.message = `Too short (${wordCount} words), aim for 1000+`;
    }
    
    // Check keyword density (1-2% is ideal)
    const densities = Object.values(article.keyword_density || {}).map(Number);
    const avgDensity = densities.length > 0 
      ? densities.reduce((a: any, b: any) => a + b, 0) / densities.length 
      : 0;
    
    if (densities.length === 0) {
      details.keywords.score = 0;
      details.keywords.status = 'error';
      details.keywords.message = 'No keywords detected';
    } else if (avgDensity >= 1 && avgDensity <= 2) {
      details.keywords.score = 25;
      details.keywords.status = 'success';
      details.keywords.message = `Perfect density (${avgDensity.toFixed(1)}%)`;
    } else if (avgDensity >= 0.5 && avgDensity <= 2.5) {
      details.keywords.score = 15;
      details.keywords.status = 'warning';
      details.keywords.message = `Good density (${avgDensity.toFixed(1)}%), ideal is 1-2%`;
    } else if (avgDensity < 0.5) {
      details.keywords.score = 5;
      details.keywords.status = 'error';
      details.keywords.message = `Too low (${avgDensity.toFixed(1)}%), aim for 1-2%`;
    } else {
      details.keywords.score = 5;
      details.keywords.status = 'error';
      details.keywords.message = `Too high (${avgDensity.toFixed(1)}%), aim for 1-2%`;
    }
    
    details.total = details.title.score + details.meta.score + 
                   details.content.score + details.keywords.score;
    
    return details;
  };

  const seoDetails = calculateSeoDetails();
  const seoScore = seoDetails.total;

  const calculateAeoDetails = () => {
    const cleanContent = cleanHtmlContent(article.content_html);

    const details = {
      schema: { score: 0, max: 25, checks: [] as any[] },
      dualMarkup: { score: 0, max: 25, checks: [] as any[] },
      answerQuality: { score: 0, max: 25, checks: [] as any[] },
      attribution: { score: 0, max: 25, checks: [] as any[] },
      total: 0
    };

    // 1. Schema.org Markup (25 points)
    const hasJsonLd = cleanContent.includes('application/ld+json');
    const hasFaqSchema = cleanContent.includes('"@type":"FAQPage') || cleanContent.includes('"@type": "FAQPage');
    const hasArticleSchema = cleanContent.includes('"@type":"Article') || cleanContent.includes('"@type": "Article');
    const hasPersonSchema = cleanContent.includes('"@type":"Person') || cleanContent.includes('"@type": "Person');

    details.schema.checks = [
      { name: 'JSON-LD Present', pass: hasJsonLd },
      { name: 'FAQPage Schema', pass: hasFaqSchema },
      { name: 'Article Schema', pass: hasArticleSchema },
      { name: 'Person Entities', pass: hasPersonSchema }
    ];
    details.schema.score = details.schema.checks.filter(c => c.pass).length * 6.25;

    // 2. Dual Markup (25 points)
    const hasItemscope = cleanContent.includes('itemscope');
    const hasItemprop = cleanContent.includes('itemprop');
    const hasItemtype = cleanContent.includes('itemtype');
    const dataMatches = hasJsonLd && hasItemscope; // Simplified check

    details.dualMarkup.checks = [
      { name: 'HTML Microdata (itemscope)', pass: hasItemscope },
      { name: 'Microdata Properties (itemprop)', pass: hasItemprop },
      { name: 'Microdata Types (itemtype)', pass: hasItemtype },
      { name: 'JSON-LD & Microdata Present', pass: dataMatches }
    ];
    details.dualMarkup.score = details.dualMarkup.checks.filter(c => c.pass).length * 6.25;

    // 3. Answer Quality (25 points)
    const h2Count = (cleanContent.match(/<h2[^>]*>/g) || []).length;
    const hasQuestions = h2Count >= 3;
    const avgAnswerLength = article.word_count / (h2Count || 1);
    const answersLongEnough = avgAnswerLength >= 150;
    const hasBlockquotes = cleanContent.includes('<blockquote');
    const noConclusionAsQuestion = !cleanContent.match(/<h2[^>]*>.*?(Conclusion|Introduction|About).*?<\/h2>/i);

    details.answerQuality.checks = [
      { name: `Multiple Questions (${h2Count} found)`, pass: hasQuestions },
      { name: `Answers ≥150 words avg (${Math.round(avgAnswerLength)} words)`, pass: answersLongEnough },
      { name: 'Expert Quotes Present', pass: hasBlockquotes },
      { name: 'No Generic Headings', pass: noConclusionAsQuestion }
    ];
    details.answerQuality.score = details.answerQuality.checks.filter(c => c.pass).length * 6.25;

    // 4. Entity Attribution (25 points)
    const hasCiteTag = cleanContent.includes('<cite');
    const hasReviewedBy = cleanContent.includes('reviewedBy');
    const hasAuthorAttribution = cleanContent.includes('itemprop="author"') || cleanContent.includes('itemprop="name"');
    const hasJobTitle = cleanContent.includes('itemprop="jobTitle"');

    details.attribution.checks = [
      { name: 'Cite Elements Present', pass: hasCiteTag },
      { name: 'Reviewer Attribution', pass: hasReviewedBy },
      { name: 'Author/Expert Attribution', pass: hasAuthorAttribution },
      { name: 'Job Title & Company', pass: hasJobTitle }
    ];
    details.attribution.score = details.attribution.checks.filter(c => c.pass).length * 6.25;

    details.total = details.schema.score + details.dualMarkup.score +
                   details.answerQuality.score + details.attribution.score;

    return details;
  };

  const aeoDetails = calculateAeoDetails();
  const aeoScore = aeoDetails.total;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              AI Generated Article Preview
            </span>
            <div className="flex items-center gap-2">
              <Badge variant={article.status === 'published' ? 'default' : 'secondary'}>
                {article.status}
              </Badge>
              <Badge 
                variant={seoScore >= 80 ? 'default' : seoScore >= 60 ? 'secondary' : 'outline'}
                className="flex items-center gap-1"
              >
                <TrendingUp className="h-3 w-3" />
                SEO: {seoScore}%
              </Badge>
            </div>
          </DialogTitle>
          <DialogDescription>{article.title}</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="preview" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="preview">
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </TabsTrigger>
            <TabsTrigger value="edit">
              <Edit3 className="h-4 w-4 mr-2" />
              Edit
            </TabsTrigger>
            <TabsTrigger value="html">
              <Code className="h-4 w-4 mr-2" />
              HTML
            </TabsTrigger>
            <TabsTrigger value="seo">
              <TrendingUp className="h-4 w-4 mr-2" />
              SEO
            </TabsTrigger>
            <TabsTrigger value="aeo">
              <TrendingUp className="h-4 w-4 mr-2" />
              AEO
            </TabsTrigger>
          </TabsList>

          <TabsContent value="preview" className="flex-1 overflow-y-auto">
            <div className="p-4">
              {/* Parse and render HTML content properly */}
              <div 
                className="prose prose-lg max-w-none article-content
                  prose-headings:font-bold prose-headings:text-gray-900
                  prose-h1:!text-3xl prose-h1:!text-gray-900 prose-h1:mt-0 prose-h1:mb-4
                  prose-h2:!text-2xl prose-h2:!text-gray-800 prose-h2:mt-6 prose-h2:mb-3
                  prose-h3:!text-xl prose-h3:!text-gray-700 prose-h3:mt-4 prose-h3:mb-2
                  prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-li:text-gray-700 prose-li:mb-2
                  prose-strong:text-gray-900 prose-strong:font-semibold
                  prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-4
                  prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-4"
                dangerouslySetInnerHTML={{ 
                  __html: cleanHtmlContent(article.content_html)
                }} 
              />
              <style>{`
                .article-content h1 {
                  font-size: 2rem !important;
                  line-height: 2.5rem !important;
                  font-weight: 700 !important;
                  margin-bottom: 1rem !important;
                }
                .article-content h2 {
                  font-size: 1.5rem !important;
                  line-height: 2rem !important;
                  font-weight: 600 !important;
                  margin-top: 1.5rem !important;
                  margin-bottom: 0.75rem !important;
                }
                .article-content h3 {
                  font-size: 1.25rem !important;
                  line-height: 1.75rem !important;
                  font-weight: 600 !important;
                  margin-top: 1rem !important;
                  margin-bottom: 0.5rem !important;
                }
                .article-content p {
                  margin-bottom: 1rem !important;
                }
                .article-content ul, .article-content ol {
                  margin-bottom: 1rem !important;
                  padding-left: 1.5rem !important;
                }
                .article-content a {
                  color: #2563eb !important;
                  text-decoration: underline !important;
                  font-weight: 500 !important;
                  transition: color 0.2s ease !important;
                }
                .article-content a:hover {
                  color: #1d4ed8 !important;
                  text-decoration: underline !important;
                }
              `}</style>
            </div>
          </TabsContent>

          <TabsContent value="edit" className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto p-4 space-y-4">
            <div>
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="edit-meta">Meta Description</Label>
              <Textarea
                id="edit-meta"
                value={editedMetaDescription}
                onChange={(e) => setEditedMetaDescription(e.target.value)}
                rows={2}
                className="mt-1"
              />
              <div className="text-xs text-muted-foreground mt-1">
                {editedMetaDescription.length}/160 characters
              </div>
            </div>

            <div>
              <Label htmlFor="edit-content">Content (HTML)</Label>
              <Textarea
                id="edit-content"
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                rows={20}
                className="mt-1 font-mono text-xs"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setEditedContent(cleanHtmlContent(article.content_html));
                  setEditedTitle(article.title);
                  setEditedMetaDescription(article.meta_description || '');
                }}
              >
                Reset
              </Button>
            </div>
            </div>
          </TabsContent>

          <TabsContent value="html" className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto relative">
            <Button
              variant="outline"
              size="sm"
              className="absolute top-2 right-2 z-10"
              onClick={handleCopy}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </>
              )}
            </Button>
            <pre className="p-4 bg-muted rounded-lg overflow-x-auto">
              <code className="text-xs">{cleanHtmlContent(article.content_html)}</code>
            </pre>
            </div>
          </TabsContent>

          <TabsContent value="seo" className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto p-4">
            <div className="space-y-6">
              {/* SEO Score */}
              <div className="bg-muted p-4 rounded-lg">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  Overall SEO Score
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">
                        <p className="text-sm">
                          The SEO Score is calculated based on:
                        </p>
                        <ul className="text-sm mt-2 space-y-1">
                          <li>• Title length (50-60 chars ideal): 25 points</li>
                          <li>• Meta description (150-160 chars): 25 points</li>
                          <li>• Word count (1000+ words): 25 points</li>
                          <li>• Keyword density (1-2% ideal): 25 points</li>
                        </ul>
                        <p className="text-sm mt-2">
                          Score of 80+ is excellent, 60+ is good.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </h3>
                <div className="flex items-center gap-4">
                  <div className="text-3xl font-bold">{seoScore}%</div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          seoScore >= 80
                            ? 'bg-green-500'
                            : seoScore >= 60
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                        }`}
                        style={{ width: `${seoScore}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* SEO Breakdown */}
              <div className="space-y-4">
                <h4 className="font-semibold">SEO Score Breakdown</h4>
                
                {/* Title Score */}
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h5 className="font-medium">Title Optimization</h5>
                      <p className={`text-sm mt-1 ${
                        seoDetails.title.status === 'success' ? 'text-green-600' :
                        seoDetails.title.status === 'warning' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {seoDetails.title.message}
                      </p>
                    </div>
                    <Badge variant={
                      seoDetails.title.status === 'success' ? 'default' :
                      seoDetails.title.status === 'warning' ? 'secondary' :
                      'destructive'
                    }>
                      {seoDetails.title.score}/{seoDetails.title.max} points
                    </Badge>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${
                        seoDetails.title.status === 'success' ? 'bg-green-500' :
                        seoDetails.title.status === 'warning' ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${(seoDetails.title.score / seoDetails.title.max) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Meta Description Score */}
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h5 className="font-medium">Meta Description</h5>
                      <p className={`text-sm mt-1 ${
                        seoDetails.meta.status === 'success' ? 'text-green-600' :
                        seoDetails.meta.status === 'warning' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {seoDetails.meta.message}
                      </p>
                    </div>
                    <Badge variant={
                      seoDetails.meta.status === 'success' ? 'default' :
                      seoDetails.meta.status === 'warning' ? 'secondary' :
                      'destructive'
                    }>
                      {seoDetails.meta.score}/{seoDetails.meta.max} points
                    </Badge>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${
                        seoDetails.meta.status === 'success' ? 'bg-green-500' :
                        seoDetails.meta.status === 'warning' ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${(seoDetails.meta.score / seoDetails.meta.max) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Content Length Score */}
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h5 className="font-medium">Content Length</h5>
                      <p className={`text-sm mt-1 ${
                        seoDetails.content.status === 'success' ? 'text-green-600' :
                        seoDetails.content.status === 'warning' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {seoDetails.content.message}
                      </p>
                    </div>
                    <Badge variant={
                      seoDetails.content.status === 'success' ? 'default' :
                      seoDetails.content.status === 'warning' ? 'secondary' :
                      'destructive'
                    }>
                      {seoDetails.content.score}/{seoDetails.content.max} points
                    </Badge>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${
                        seoDetails.content.status === 'success' ? 'bg-green-500' :
                        seoDetails.content.status === 'warning' ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${(seoDetails.content.score / seoDetails.content.max) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Keyword Density Score */}
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h5 className="font-medium">Keyword Optimization</h5>
                      <p className={`text-sm mt-1 ${
                        seoDetails.keywords.status === 'success' ? 'text-green-600' :
                        seoDetails.keywords.status === 'warning' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {seoDetails.keywords.message}
                      </p>
                    </div>
                    <Badge variant={
                      seoDetails.keywords.status === 'success' ? 'default' :
                      seoDetails.keywords.status === 'warning' ? 'secondary' :
                      'destructive'
                    }>
                      {seoDetails.keywords.score}/{seoDetails.keywords.max} points
                    </Badge>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${
                        seoDetails.keywords.status === 'success' ? 'bg-green-500' :
                        seoDetails.keywords.status === 'warning' ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${(seoDetails.keywords.score / seoDetails.keywords.max) * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Word Count</h4>
                  <div className="text-2xl font-bold">{article.word_count}</div>
                </div>
                
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Title Length</h4>
                  <div className="text-2xl font-bold">{article.title.length}</div>
                  <div className="text-xs text-muted-foreground">
                    Ideal: 50-60 characters
                  </div>
                </div>
              </div>

              {/* Keyword Density */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3">Keyword Density</h4>
                <div className="space-y-2">
                  {Object.entries(article.keyword_density || {}).map(([keyword, density]) => (
                    <div key={keyword} className="flex justify-between items-center">
                      <span className="text-sm">{keyword}</span>
                      <Badge variant="outline">{density}%</Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Meta Description */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-2">Meta Description</h4>
                <p className="text-sm text-muted-foreground">{article.meta_description}</p>
                <div className="text-xs text-muted-foreground mt-2">
                  {(article.meta_description || '').length}/160 characters
                </div>
              </div>
            </div>
            </div>
          </TabsContent>

          <TabsContent value="aeo" className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto p-4">
            <div className="space-y-6">
              {/* AEO Score */}
              <div className="bg-muted p-4 rounded-lg">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  Overall AEO Score
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">
                        <p className="text-sm">
                          AEO (Answer Engine Optimization) measures how well your content performs in AI search engines like ChatGPT, Perplexity, and Claude.
                        </p>
                        <ul className="text-sm mt-2 space-y-1">
                          <li>• Schema.org markup: 25 points</li>
                          <li>• Dual markup validation: 25 points</li>
                          <li>• Answer quality: 25 points</li>
                          <li>• Entity attribution: 25 points</li>
                        </ul>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </h3>
                <div className="flex items-center gap-4">
                  <div className="text-3xl font-bold">{Math.round(aeoScore)}%</div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          aeoScore >= 80
                            ? 'bg-green-500'
                            : aeoScore >= 60
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                        }`}
                        style={{ width: `${aeoScore}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* AEO Breakdown */}
              <div className="space-y-4">
                <h4 className="font-semibold">AEO Score Breakdown</h4>

                {/* Schema.org Markup */}
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <h5 className="font-medium">Schema.org Markup</h5>
                    <Badge variant={aeoDetails.schema.score >= 20 ? 'default' : aeoDetails.schema.score >= 12 ? 'secondary' : 'destructive'}>
                      {Math.round(aeoDetails.schema.score)}/{aeoDetails.schema.max} points
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {aeoDetails.schema.checks.map((check: any, i: number) => (
                      <div key={i} className="flex items-center gap-2">
                        {check.pass ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-600" />
                        )}
                        <span className="text-sm">{check.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Dual Markup */}
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <h5 className="font-medium">Dual Markup (JSON-LD + Microdata)</h5>
                    <Badge variant={aeoDetails.dualMarkup.score >= 20 ? 'default' : aeoDetails.dualMarkup.score >= 12 ? 'secondary' : 'destructive'}>
                      {Math.round(aeoDetails.dualMarkup.score)}/{aeoDetails.dualMarkup.max} points
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {aeoDetails.dualMarkup.checks.map((check: any, i: number) => (
                      <div key={i} className="flex items-center gap-2">
                        {check.pass ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-600" />
                        )}
                        <span className="text-sm">{check.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Answer Quality */}
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <h5 className="font-medium">Answer Quality</h5>
                    <Badge variant={aeoDetails.answerQuality.score >= 20 ? 'default' : aeoDetails.answerQuality.score >= 12 ? 'secondary' : 'destructive'}>
                      {Math.round(aeoDetails.answerQuality.score)}/{aeoDetails.answerQuality.max} points
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {aeoDetails.answerQuality.checks.map((check: any, i: number) => (
                      <div key={i} className="flex items-center gap-2">
                        {check.pass ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-600" />
                        )}
                        <span className="text-sm">{check.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Entity Attribution */}
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <h5 className="font-medium">Entity & Attribution</h5>
                    <Badge variant={aeoDetails.attribution.score >= 20 ? 'default' : aeoDetails.attribution.score >= 12 ? 'secondary' : 'destructive'}>
                      {Math.round(aeoDetails.attribution.score)}/{aeoDetails.attribution.max} points
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {aeoDetails.attribution.checks.map((check: any, i: number) => (
                      <div key={i} className="flex items-center gap-2">
                        {check.pass ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-600" />
                        )}
                        <span className="text-sm">{check.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              {aeoScore < 80 && (
                <div className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Recommendations
                  </h4>
                  <ul className="text-sm space-y-1">
                    {!aeoDetails.schema.checks[0].pass && <li>• Add JSON-LD structured data to the article</li>}
                    {!aeoDetails.dualMarkup.checks[0].pass && <li>• Add HTML microdata (itemscope, itemprop) to match JSON-LD</li>}
                    {!aeoDetails.answerQuality.checks[1].pass && <li>• Expand answers to at least 150 words each</li>}
                    {!aeoDetails.attribution.checks[1].pass && <li>• Add reviewer attribution with schema.org markup</li>}
                  </ul>
                </div>
              )}
            </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-between gap-3 pt-4 border-t">
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export HTML
            </Button>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button onClick={handlePublish} disabled={publishing}>
              {publishing ? (
                'Publishing...'
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Publish Article
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}